<template>
  <div v-if="compositionType" class="bg-white rounded-lg">
    <div class="flex items-center justify-between mb-2">
      <h4 class="text-base font-semibold text-black">
        Composition <span class="text-red-500">*</span>
      </h4>
      <div class="text-xs text-black">
        Type: {{ compositionType === "autres" ? "Autres" : "Chaussures" }}
      </div>
    </div>

    <!-- Message d'avertissement si compositions manquantes -->
    <div
      v-if="compositionType && modelValue.length === 0"
      class="text-xs text-red-600 mb-2"
    >
      ⚠️ Les compositions sont obligatoires.
    </div>

    <!-- Message d'avertissement spécifique pour les chaussures -->
    <div
      v-if="compositionType === 'chaussures' && !isChaussuresValid"
      class="text-xs text-red-600 mb-2"
    >
      ⚠️ Sélectionnez au moins un matériau pour chaque zone : Tige, Doublure et
      Semelle.
    </div>

    <!-- Interface pour "autres" -->
    <div v-if="compositionType === 'autres'" class="space-y-2">
      <div v-if="recentCompositionSets.length" class="space-y-1">
        <p class="text-xs font-medium text-gray-600">Compositions récentes</p>
        <div class="flex flex-wrap gap-2">
          <button
            v-for="set in recentCompositionSets"
            :key="set.label"
            type="button"
            class="px-3 py-1.5 text-xs text-left text-blue-800 bg-blue-50 border border-blue-200 rounded-full hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
            :title="`Réutiliser : ${set.label}`"
            @click="applyRecentCompositionSet(set)"
          >
            {{ set.label }}
          </button>
        </div>
      </div>
      <div class="flex gap-2">
        <select
          v-model="selectedLocalisation"
          class="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 text-sm text-black"
        >
          <option value="">Sélectionner ...</option>
          <option v-if="!hasProduitInLocalisations" value="0-Produit">
            Produit
          </option>
          <option
            v-for="localisation in localisations"
            :key="localisation.id_composition_localisation"
            :value="`${localisation.id_composition_localisation}-${localisation.libelle}`"
          >
            {{ localisation.libelle }}
          </option>
        </select>
        <div class="flex-1 relative">
          <input
            ref="compositionInputRef"
            v-model="newCompositionText"
            type="text"
            placeholder="-- Tapez une composition --"
            class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 text-sm text-black"
            @input="handleInput"
            @keydown.down.prevent="handleArrowDown"
            @keydown.up.prevent="handleArrowUp"
            @keydown.enter.prevent="handleEnter"
            @keydown.esc.prevent="hideAutocomplete"
            @blur="handleBlur"
            @focus="handleFocus"
          />

          <Teleport to="body">
            <div
              v-if="showDropdown"
              class="fixed z-[10050] bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto"
              :style="dropdownStyle"
            >
              <template
                v-for="(option, index) in displayOptions"
                :key="`${option.id}-${option.fromRecent ? 'recent' : 'suggestion'}`"
              >
                <div
                  v-if="index === 0 && option.fromRecent"
                  class="px-3 py-1.5 text-[11px] uppercase tracking-wide text-gray-500 bg-gray-50 border-b border-gray-100"
                >
                  Récemment utilisées
                </div>
                <div
                  v-else-if="
                    !option.fromRecent &&
                    (index === 0 || displayOptions[index - 1]?.fromRecent)
                  "
                  class="px-3 py-1.5 text-[11px] uppercase tracking-wide text-gray-500 bg-gray-50 border-b border-gray-100"
                >
                  Suggestions
                </div>
                <div
                  @mousedown.prevent="selectFromAutocomplete(option)"
                  :class="[
                    'px-3 py-2 cursor-pointer text-sm border-b border-gray-100 last:border-b-0 flex items-center justify-between gap-2',
                    index === highlightedIndex
                      ? 'bg-blue-100'
                      : 'hover:bg-gray-100',
                  ]"
                >
                  <span>{{ option.label }}</span>
                  <i
                    v-if="option.fromRecent"
                    class="fas fa-history shrink-0 text-[10px] text-gray-400"
                    title="Composition récente"
                  ></i>
                </div>
              </template>
            </div>
          </Teleport>

          <!-- Loading indicator -->
          <div v-if="isLoadingAutocomplete" class="absolute right-2 top-2">
            <i class="fas fa-spinner fa-spin text-black"></i>
          </div>
        </div>
        <!-- Bouton supprimé: l'ajout se fait automatiquement en cliquant une option -->
      </div>

      <!-- Liste des compositions ajoutées -->
      <div v-if="modelValue.length > 0" class="space-y-2">
        <div
          v-for="(group, localisationId) in groupedCompositions"
          :key="localisationId"
          class="border border-gray-300 rounded-lg p-2 bg-white"
        >
          <h5 class="text-sm font-medium text-black mb-1">
            {{ getLocalisationName(parseInt(localisationId)) }}
          </h5>
          <div class="space-y-2">
            <div
              v-for="comp in group"
              :key="`${comp.localisationId}-${comp.compositionId}-${
                comp.compositionName || ''
              }`"
              class="flex items-center gap-2"
            >
              <span
                class="flex-1 px-3 py-2 bg-gray-100 rounded text-sm text-black"
              >
                {{ getCompositionName(comp) }}
              </span>
              <div class="relative">
                <input
                  :data-composition-percentage="`${comp.localisationId}-${comp.compositionId}`"
                  :value="comp.percentage !== '' && comp.percentage != null ? +Number(comp.percentage).toFixed(2) : comp.percentage"
                  type="text"
                  inputmode="decimal"
                  placeholder="0"
                  class="peer w-20 px-2 py-1 pr-6 border border-gray-300 focus:border-blue-600 focus:outline-none rounded text-sm text-center text-black"
                  @change="(e) => updatePercentage(comp, e)"
                  @keydown.enter.prevent="(e) => finishPercentage(comp, e)"
                />
                <span
                  class="absolute right-2 top-1/2 transform -translate-y-1/2 text-black peer-focus:text-black text-sm pointer-events-none"
                  >%</span
                >
              </div>
              <button
                type="button"
                @click="removeComposition(comp)"
                class="px-2 py-1 bg-red-500 text-white rounded hover:bg-red-600 text-sm"
              >
                <i class="fas fa-trash"></i>
              </button>
            </div>
          </div>
        </div>

        <!-- Validation des pourcentages par localisation -->
        <div v-if="!isPercentagesValid" class="text-sm text-red-600">
          ⚠️ Chaque partie doit totaliser exactement 100% et aucun élément ne
          peut être à 0%
        </div>
        <div
          v-else-if="isPercentagesValid && modelValue.length > 0"
          class="text-sm text-green-600"
        >
          ✓ Composition valide
        </div>
      </div>
    </div>

    <!-- Interface pour "chaussures" -->
    <div v-else-if="compositionType === 'chaussures'" class="space-y-2">
      <div class="text-xs text-black mb-2">
        Sélectionnez les compositions pour chaque partie :
      </div>

      <div class="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div
          v-for="localisation in localisations"
          :key="localisation.id_composition_localisation"
          class="border border-gray-300 rounded-lg p-2 bg-gray-50"
        >
          <h5 class="text-sm font-medium text-black mb-2 text-center">
            {{ localisation.libelle }}
          </h5>
          <div class="space-y-2">
            <label
              v-for="composition in compositionsList"
              :key="composition.id_composition"
              class="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 p-1 rounded"
            >
              <input
                type="checkbox"
                :checked="
                  isCompositionSelected(
                    localisation.id_composition_localisation,
                    composition.id_composition,
                  )
                "
                @change="
                  toggleChaussureComposition(
                    localisation.id_composition_localisation,
                    composition.id_composition,
                  )
                "
                class="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span class="text-sm text-black">{{ composition.libelle }}</span>
            </label>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, ref, watch } from "vue";
import { useNotification } from "@/composables/useNotification";
import type {
  CompositionItem as CompositionData,
  CompositionLocalisation,
  CompositionMateriau,
  CompositionAutocompleteOption,
  CompositionType,
} from "@/types/mel-components";
import {
  filterRecentCompositions,
  getRecentCompositionSets,
  rememberComposition,
  type RecentCompositionSet,
} from "@/utils/melCompositionHistory";

const { error } = useNotification();

interface Props {
  modelValue: CompositionData[];
  compositionType: CompositionType;
  localisations: CompositionLocalisation[];
  compositionsList: CompositionMateriau[];
  autocompleteOptions: CompositionAutocompleteOption[];
  showAutocompleteDropdown: boolean;
  isLoadingAutocomplete: boolean;
}

interface Emits {
  (e: "update:modelValue", value: CompositionData[]): void;
  (e: "composition-input", value: string, localisationId: string | null): void;
  (e: "hide-dropdown"): void;
  (e: "select-autocomplete", value: CompositionAutocompleteOption): void;
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

// Vérifier si "Produit" existe déjà dans les localisations de la DB
const hasProduitInLocalisations = computed(() => {
  return props.localisations.some(
    (loc) => loc.libelle?.toLowerCase() === "produit",
  );
});

// Trouver la localisation "Produit" de la DB
const produitLocalisation = computed(() => {
  return props.localisations.find(
    (loc) => loc.libelle?.toLowerCase() === "produit",
  );
});

// Valeur par défaut : utiliser la localisation "Produit" de la DB si elle existe, sinon "0-Produit"
const defaultLocalisationValue = computed(() => {
  if (produitLocalisation.value) {
    return `${produitLocalisation.value.id_composition_localisation}-${produitLocalisation.value.libelle}`;
  }
  return "0-Produit";
});

const selectedLocalisation = ref("");
const newCompositionText = ref("");
const selectedCompositionId = ref<number>(0);
const highlightedIndex = ref(-1);
const isInputFocused = ref(false);
const suppressDropdownAfterSelection = ref(false);
const compositionInputRef = ref<HTMLInputElement | null>(null);
const recentCompositionSets = ref<RecentCompositionSet[]>(
  getRecentCompositionSets(),
);
const dropdownStyle = ref({
  top: "0px",
  left: "0px",
  width: "0px",
});

const selectedLocalisationId = computed(() => {
  if (!selectedLocalisation.value) return null;
  const id = Number.parseInt(selectedLocalisation.value.split("-")[0], 10);
  return Number.isFinite(id) ? id : null;
});

const excludedCompositionIds = computed(() =>
  props.modelValue
    .filter((comp) =>
      selectedLocalisationId.value == null
        ? true
        : comp.localisationId === selectedLocalisationId.value,
    )
    .map((comp) => comp.compositionId)
    .filter((id) => id > 0),
);

const displayOptions = computed(() => {
  const recent = filterRecentCompositions(
    newCompositionText.value,
    excludedCompositionIds.value,
  ).map((option) => ({ ...option, fromRecent: true as const }));
  const recentIds = new Set(recent.map((option) => option.id));
  const suggestions = props.autocompleteOptions
    .filter((option) => !recentIds.has(option.id))
    .filter((option) => !excludedCompositionIds.value.includes(option.id))
    .map((option) => ({ ...option, fromRecent: false as const }));
  return [...recent, ...suggestions];
});

const showDropdown = computed(
  () =>
    isInputFocused.value &&
    !suppressDropdownAfterSelection.value &&
    displayOptions.value.length > 0 &&
    (props.showAutocompleteDropdown ||
      displayOptions.value.some((option) => option.fromRecent)),
);

function updateDropdownPosition() {
  const element = compositionInputRef.value;
  if (!element) return;
  const rect = element.getBoundingClientRect();
  dropdownStyle.value = {
    top: `${rect.bottom + 4}px`,
    left: `${rect.left}px`,
    width: `${rect.width}px`,
  };
}

function handleDropdownReposition() {
  if (!showDropdown.value) {
    return;
  }
  updateDropdownPosition();
}

if (typeof window !== "undefined") {
  window.addEventListener("resize", handleDropdownReposition);
  window.addEventListener("scroll", handleDropdownReposition, true);
}

onBeforeUnmount(() => {
  if (typeof window === "undefined") return;
  window.removeEventListener("resize", handleDropdownReposition);
  window.removeEventListener("scroll", handleDropdownReposition, true);
});

const groupedCompositions = computed(() => {
  const groups: Record<number, CompositionData[]> = {};
  props.modelValue.forEach((comp) => {
    if (!groups[comp.localisationId]) {
      groups[comp.localisationId] = [];
    }
    groups[comp.localisationId].push(comp);
  });
  return groups;
});

const isPercentagesValid = computed(() => {
  if (props.compositionType !== "autres") return true;

  const localisationGroups: { [key: number]: number } = {};

  // Règles:
  // - Chaque pourcentage doit être strictement > 0
  // - La somme par localisation doit être exactement 100
  for (const comp of props.modelValue) {
    const percentage = Number(comp.percentage) || 0;
    // Interdire 0% ou valeurs négatives
    if (percentage <= 0) {
      return false;
    }
    if (localisationGroups[comp.localisationId]) {
      localisationGroups[comp.localisationId] += percentage;
    } else {
      localisationGroups[comp.localisationId] = percentage;
    }
  }

  for (const localisationId in localisationGroups) {
    if (Math.abs(localisationGroups[localisationId] - 100) > 0.01) {
      return false;
    }
  }

  return true;
});

const isChaussuresValid = computed(() => {
  if (props.compositionType !== "chaussures") return true;

  const localisationIds = props.localisations.map(
    (loc) => loc.id_composition_localisation,
  );
  return localisationIds.every((localisationId) =>
    props.modelValue.some((comp) => comp.localisationId === localisationId),
  );
});

const getLocalisationName = (localisationId: number): string => {
  if (localisationId === 0) {
    return "Produit";
  }
  const localisation = props.localisations.find(
    (loc) => loc.id_composition_localisation === localisationId,
  );
  return localisation ? localisation.libelle : `Localisation ${localisationId}`;
};

// Retourne le libellé affichable d'une composition
const getCompositionName = (comp: CompositionData): string => {
  // Priorité au nom déjà présent dans l'élément (souvent saisi via l'autocomplete)
  if (comp.compositionName && comp.compositionName.trim() !== "") {
    return comp.compositionName;
  }
  // Sinon, on tente de retrouver le libellé via la liste des compositions disponibles
  const compId = Number(comp.compositionId);
  const found = props.compositionsList.find(
    (c) => Number(c.id_composition) === compId,
  );
  return found ? found.libelle : `Composition ${comp.compositionId}`;
};

const handleInput = () => {
  suppressDropdownAfterSelection.value = false;
  selectedCompositionId.value = 0;
  highlightedIndex.value = -1;
  updateDropdownPosition();
  // Extraire l'ID de la localisation sélectionnée
  const localisationId = selectedLocalisation.value
    ? selectedLocalisation.value.split("-")[0]
    : null;
  emit("composition-input", newCompositionText.value, localisationId);
};

const handleFocus = () => {
  isInputFocused.value = true;
  suppressDropdownAfterSelection.value = false;
  handleInput();
};

const handleBlur = () => {
  isInputFocused.value = false;
  emit("hide-dropdown");
};

watch(
  () => [showDropdown.value, displayOptions.value.length],
  () => {
    if (!showDropdown.value) {
      return;
    }
    void nextTick(updateDropdownPosition);
  }
);

const handleArrowDown = () => {
  if (showDropdown.value && displayOptions.value.length > 0) {
    highlightedIndex.value = Math.min(
      highlightedIndex.value + 1,
      displayOptions.value.length - 1,
    );
  }
};

const handleArrowUp = () => {
  if (showDropdown.value && displayOptions.value.length > 0) {
    highlightedIndex.value = Math.max(highlightedIndex.value - 1, 0);
  }
};

const handleEnter = () => {
  if (showDropdown.value && displayOptions.value.length) {
    const index = highlightedIndex.value >= 0 ? highlightedIndex.value : 0;
    selectFromAutocomplete(displayOptions.value[index]);
  } else {
    addComposition();
  }
};

const hideAutocomplete = () => {
  highlightedIndex.value = -1;
  emit("hide-dropdown");
};

const selectFromAutocomplete = (composition: CompositionAutocompleteOption) => {
  // Renseigner l'entrée et l'ID sélectionné
  newCompositionText.value = composition.label;
  selectedCompositionId.value = composition.id;
  emit("select-autocomplete", composition);
  // Si aucune localisation choisie, utiliser la valeur par défaut (Produit)
  if (!selectedLocalisation.value) {
    selectedLocalisation.value = defaultLocalisationValue.value;
  }
  // Ajouter automatiquement dès la sélection
  addComposition();
  // Fermer le dropdown
  emit("hide-dropdown");
};

const addComposition = () => {
  if (!selectedLocalisation.value || !newCompositionText.value.trim()) return;

  const [localisationId] = selectedLocalisation.value.split("-");
  const compositionName = newCompositionText.value.trim();

  // ⚠️ VALIDATION : La composition DOIT exister dans la DB (autocomplete)
  if (selectedCompositionId.value === 0) {
    error(
      "⚠️ Veuillez sélectionner une composition valide depuis la liste. Les compositions personnalisées ne sont pas autorisées.",
    );
    return;
  }

  const exists = props.modelValue.some(
    (comp) =>
      comp.localisationId === parseInt(localisationId) &&
      comp.compositionId === selectedCompositionId.value &&
      comp.compositionName === compositionName,
  );

  if (exists) {
    error("Cette composition existe déjà pour cette localisation.");
    return;
  }

  const hasCompositionAtLocation = props.modelValue.some(
    (comp) => comp.localisationId === parseInt(localisationId),
  );
  rememberComposition({
    id: selectedCompositionId.value,
    label: compositionName,
  });
  const updatedCompositions: CompositionData[] = [
    ...props.modelValue,
    {
      localisationId: parseInt(localisationId),
      compositionId: selectedCompositionId.value,
      compositionName: compositionName,
      percentage: hasCompositionAtLocation ? "" : 100,
    },
  ];

  emit("update:modelValue", updatedCompositions);
  newCompositionText.value = "";
  selectedCompositionId.value = 0;
  highlightedIndex.value = -1;
  suppressDropdownAfterSelection.value = true;
  emit("hide-dropdown");
  void nextTick(() => {
    const selector = `[data-composition-percentage="${localisationId}-${updatedCompositions[updatedCompositions.length - 1].compositionId}"]`;
    const percentageInput = document.querySelector<HTMLInputElement>(selector);
    percentageInput?.focus();
    percentageInput?.select();
  });
};

const applyRecentCompositionSet = (set: RecentCompositionSet) => {
  const localisationId = selectedLocalisationId.value ?? 0;
  const otherLocations = props.modelValue.filter(
    (composition) => composition.localisationId !== localisationId,
  );
  emit("update:modelValue", [
    ...otherLocations,
    ...set.items.map((item) => ({ ...item, localisationId })),
  ]);
  emit("hide-dropdown");
};

const removeComposition = (composition: CompositionData) => {
  const updatedCompositions = props.modelValue.filter(
    (comp) =>
      !(
        comp.localisationId === composition.localisationId &&
        comp.compositionId === composition.compositionId &&
        comp.compositionName === composition.compositionName
      ),
  );
  const remainingAtLocation = updatedCompositions.filter(
    (item) => item.localisationId === composition.localisationId,
  );
  if (remainingAtLocation.length === 1) {
    remainingAtLocation[0].percentage = 100;
  }
  emit("update:modelValue", updatedCompositions);
};

const updatePercentage = (comp: CompositionData, event: Event) => {
  const target = event.target as HTMLInputElement;
  // Accepter virgule et point comme séparateur décimal
  const value = target.value.replace(',', '.');

  // Convertir en nombre, en gardant la valeur exacte saisie
  let numValue: number | "" = "";
  if (value !== "") {
    const parsed = parseFloat(value);
    if (!isNaN(parsed)) {
      const rounded = Math.round(parsed * 100) / 100;
      numValue = Math.max(0.01, Math.min(100, rounded));
    }
  }

  // Mettre à jour la composition
  comp.percentage = numValue;

  // Émettre la mise à jour
  emit("update:modelValue", [...props.modelValue]);
};

const finishPercentage = (comp: CompositionData, event: Event) => {
  updatePercentage(comp, event);
  void nextTick(() => compositionInputRef.value?.focus());
};

const isCompositionSelected = (
  localisationId: number,
  compositionId: number,
): boolean => {
  return props.modelValue.some(
    (comp) =>
      comp.localisationId === localisationId &&
      comp.compositionId === compositionId,
  );
};

const toggleChaussureComposition = (
  localisationId: number,
  compositionId: number,
) => {
  const existingIndex = props.modelValue.findIndex(
    (comp) =>
      comp.localisationId === localisationId &&
      comp.compositionId === compositionId,
  );

  if (existingIndex > -1) {
    // Retirer la composition
    const updatedCompositions = [...props.modelValue];
    updatedCompositions.splice(existingIndex, 1);
    emit("update:modelValue", updatedCompositions);
  } else {
    // Ajouter la composition
    const composition = props.compositionsList.find(
      (comp) => comp.id_composition === compositionId,
    );
    if (composition) {
      const updatedCompositions = [
        ...props.modelValue,
        {
          localisationId: localisationId,
          compositionId: compositionId,
          compositionName: composition.libelle,
          percentage: "",
        },
      ];
      emit("update:modelValue", updatedCompositions);
    }
  }
};

// Watcher pour maintenir "Produit" comme valeur par défaut
watch(
  () => [props.compositionType, defaultLocalisationValue.value],
  ([newType, defaultValue]) => {
    if (newType === "autres") {
      if (!selectedLocalisation.value || selectedLocalisation.value === "") {
        selectedLocalisation.value = defaultValue;
      }
    }
  },
  { immediate: true },
);

// Watcher pour initialiser la valeur par défaut quand les localisations sont chargées
watch(
  () => props.localisations,
  (newLocalisations) => {
    if (
      newLocalisations &&
      newLocalisations.length > 0 &&
      props.compositionType === "autres"
    ) {
      if (!selectedLocalisation.value || selectedLocalisation.value === "") {
        selectedLocalisation.value = defaultLocalisationValue.value;
      }
    }
  },
  { immediate: true },
);

// Watcher pour réinitialiser à "Produit" si la valeur est vide
watch(selectedLocalisation, (newValue) => {
  if (props.compositionType === "autres" && (!newValue || newValue === "")) {
    selectedLocalisation.value = defaultLocalisationValue.value;
  }
});

</script>
